import { type ReactNode, useState } from 'react';
import { trpc } from '@/trpc/client';

interface StateData {
  state: string;
  linkedAccountId?: string;
  cameViaFollow?: boolean;
  summary?: string;
  nickname?: string;
  createdAt: string;
  lastEventAt: string;
}

interface HistoryTurn {
  role: 'user' | 'assistant';
  content: string;
  eventId?: string;
}

interface SendLogEntry {
  idempotencyKey: string;
  userId: string;
  messageKey?: string;
  accountId?: string;
  sentAt?: string;
  createdAt?: string;
  [k: string]: unknown;
}

interface ObservationPanelProps {
  userId: string;
  state: StateData | null;
  history: HistoryTurn[];
  sendLog: SendLogEntry[];
  eventLog: SendLogEntry[];
}

interface SectionProps {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

function Section({ title, defaultOpen = true, children }: SectionProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50"
      >
        <span>{title}</span>
        <span className="text-xs text-gray-500">{open ? '−' : '+'}</span>
      </button>
      {open ? <div className="border-t border-gray-100 px-4 py-3 text-sm">{children}</div> : null}
    </section>
  );
}

function StateSection({ state }: { state: StateData | null }): JSX.Element {
  if (!state) {
    return <div className="text-gray-500">状態レコードがありません。</div>;
  }
  const rows: Array<[string, string]> = [
    ['ステート', state.state],
    ['ともだち追加経由', state.cameViaFollow ? 'はい' : 'いいえ'],
    ['紐付け先 Account', state.linkedAccountId ?? '(なし)'],
    ['表示名キャッシュ', state.nickname ?? '(なし)'],
    ['作成日時', state.createdAt],
    ['最終イベント', state.lastEventAt],
  ];
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-gray-500">{k}</dt>
          <dd className="font-mono text-gray-900">{v}</dd>
        </div>
      ))}
      {state.summary ? (
        <div className="col-span-2 mt-2">
          <div className="text-gray-500">サマリ</div>
          <div className="whitespace-pre-wrap font-mono text-xs text-gray-800">{state.summary}</div>
        </div>
      ) : null}
    </dl>
  );
}

function HistorySection({ history }: { history: HistoryTurn[] }): JSX.Element {
  if (history.length === 0) {
    return <div className="text-gray-500">会話履歴はありません。</div>;
  }
  return (
    <ol className="space-y-2">
      {history.map((turn, i) => (
        <li key={turn.eventId ?? `${i}-${turn.role}`} className="rounded bg-gray-50 p-2">
          <div className="text-xs font-semibold uppercase text-gray-500">{turn.role}</div>
          <div className="whitespace-pre-wrap font-mono text-xs text-gray-800">{turn.content}</div>
        </li>
      ))}
    </ol>
  );
}

function LogTable({ rows }: { rows: SendLogEntry[] }): JSX.Element {
  if (rows.length === 0) {
    return <div className="text-gray-500">エントリーはありません。</div>;
  }
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-gray-500">
        <tr>
          <th className="py-1 pr-2">冪等キー</th>
          <th className="py-1 pr-2">メッセージ / Account</th>
          <th className="py-1 pr-2">日時</th>
        </tr>
      </thead>
      <tbody className="font-mono">
        {rows.map((r) => (
          <tr key={r.idempotencyKey} className="border-t border-gray-100">
            <td className="py-1 pr-2 text-gray-800">{r.idempotencyKey}</td>
            <td className="py-1 pr-2 text-gray-800">{r.messageKey ?? r.accountId ?? '—'}</td>
            <td className="py-1 pr-2 text-gray-600">{r.sentAt ?? r.createdAt ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SfAccountSection({ userId }: { userId: string }): JSX.Element {
  const [show, setShow] = useState(false);
  const sfQuery = trpc.lineUser.fetchSalesforce.useQuery(
    { userId },
    { enabled: show, retry: false },
  );
  if (!show) {
    return (
      <button
        type="button"
        onClick={() => setShow(true)}
        className="rounded bg-gray-900 px-3 py-1 text-xs text-white hover:bg-gray-700"
      >
        Salesforce アカウントを取得
      </button>
    );
  }
  if (sfQuery.isLoading) return <div className="text-gray-500">読み込み中…</div>;
  if (sfQuery.error) {
    return <div className="text-red-600">エラー: {sfQuery.error.message}</div>;
  }
  const candidates = sfQuery.data?.candidates ?? [];
  if (candidates.length === 0) {
    return <div className="text-gray-500">該当する Salesforce アカウントは見つかりません。</div>;
  }
  return (
    <ul className="space-y-1 font-mono text-xs">
      {candidates.map((c: { Id: string; Name?: string }) => (
        <li key={c.Id} className="rounded bg-gray-50 p-2">
          <div className="text-gray-500">Id</div>
          <div className="text-gray-800">{c.Id}</div>
          {c.Name ? (
            <>
              <div className="mt-1 text-gray-500">Name</div>
              <div className="text-gray-800">{c.Name}</div>
            </>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Collapsible read-only observation surface for a single LINE user.
 * Five sections: State, History, SendLog, EventLog, SF Account.
 * The SF Account section fetches lazily via tRPC; the rest are passed in.
 */
export function ObservationPanel(props: ObservationPanelProps): JSX.Element {
  return (
    <div className="space-y-3">
      <Section title="状態">
        <StateSection state={props.state} />
      </Section>
      <Section title={`会話履歴 (${props.history.length})`} defaultOpen={false}>
        <HistorySection history={props.history} />
      </Section>
      <Section title={`メッセージ送信ログ (${props.sendLog.length})`} defaultOpen={false}>
        <LogTable rows={props.sendLog} />
      </Section>
      <Section title={`行動作成ログ (${props.eventLog.length})`} defaultOpen={false}>
        <LogTable rows={props.eventLog} />
      </Section>
      <Section title="Salesforce アカウント" defaultOpen={false}>
        <SfAccountSection userId={props.userId} />
      </Section>
    </div>
  );
}
